import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EnvGet } from '../../utils/EnvGet';
import BankingTypeContext from '../../utils/BankingType/context';
import { compileDisclosureText, QueryParams } from '../../utils/SiteTools';
import { demo_steps } from './data/demo_steps.json';
import * as SELECTORS from '../../constants/selectors';
import {lobRoot} from '../../utils/LobLogicModule';
import './DemoSteps.scss';

const DemoSteps = ({ content }) => {
	// Extracted STEPS from content prop
	const { steps } = content;
	
	const { t } = useTranslation('demo_steps');
	const firstLoad = useRef(true);
	const focusHeader = useRef();
	const focusSingleButton = useRef();
	const pulseCheckboxState = useRef();
	const textCopy = useRef();
	const imgDevice = useRef();
	const pageUrl = window.location.href;
	const sourceName = EnvGet();
	const mastHeadHeight = document.querySelector('.masthead') && (document.querySelector('.masthead').offsetHeight - document.querySelector('.header').offsetHeight);
	const [step, setStep] = useState(-1);
	const lastStep = steps.length;
	const [pageUrlRoute, setPageUrlRoute] = useState(pageUrl);
	if(pageUrlRoute !== pageUrl){
		setStep(-1);
		setPageUrlRoute(pageUrl);
	}
	const { bankingType } = useContext(BankingTypeContext)
	const [isImageLoaded, setImageLoaded] = useState(false);
	const [isAllStepOpen, setAllStepOpen] = useState(false);
	const [isPulseCheckboxChecked, setPulseCheckboxChecked] = useState(true);

	const [stepImage, setStepImage] = useState(null);
	const [stepImgAlt, setStepImgAlt] = useState('');
	const [stepTitle, setStepTitle] = useState('');
	const [stepCopy, setStepCopy] = useState('');
	

	let [posX, posY] = (step > -1 && step < lastStep) ? steps[step].hotspot : (bankingType === SELECTORS.MB || bankingType === SELECTORS.MPOS) ? [40, 30] : [44.5, 28];
	const hotSpotStyles = {
		left: posX + '%',
		top: posY + '%'
	}

	const imgDeviceBg = (bankingType === SELECTORS.MB || bankingType === SELECTORS.MPOS) ? SELECTORS.MB : SELECTORS.OLB;
	const imgDeviceStyle = {
		backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/step-frame-` + imgDeviceBg + `.png)`
	}
	// used in order to activate queryParams
	const activateQP = () => {
		setTimeout(() => {
			QueryParams();
		}, 150);
	};
	
	/**
	 * The function is called when the user clicks the previous button. 
	 * It sets the step to the previous step, and sets the imageLoaded variable to false. 
	 * It also focuses the header, and adds the hide class to the read more button. 
	 * It also activates the carousel 
	 */
	const prevState = () => {
		step > -1 && setStep(step - 1);
		setImageLoaded(false);
		focusHeader.current.focus();
		textCopy.current.scrollTo(0, 0);
		activateQP();
	}
	/**
	 * The nextState function is called when the user clicks the next button. 
	 * It sets the step to the next step, and sets the imageLoaded variable to false. 
	 * This is so that the image is not loaded until the user clicks the next button. 
	 * It also focuses the header element, and adds the hide class to the read more button. 
	 * This is so that the read more button is hidden until the image is loaded. 
	 * Finally, it activates the carousel
	 */
	const nextState = () => {
		step < lastStep ? setStep(step + 1) : setStep(-1);
		setImageLoaded(false);
		focusHeader.current.focus();
		textCopy.current.scrollTo(0, 0);
		activateQP();
	}

	const imageLoaded = () => setImageLoaded(true);
	const imageNotLoaded = () => setImageLoaded(true);
	
	/**
	 * When the user clicks the "View All Steps" button, the page scrolls to the top of the masthead
	 * and expands the modal of view all steps
	 */
	const viewAllSteps = () => {
		window.scrollTo({
			behavior: 'smooth',
			left: 0,
			top: mastHeadHeight
		});
		setImageLoaded(false);
		setAllStepOpen(!isAllStepOpen);
		activateQP();
	}
	
	const handleChange = event => event.target.checked ? setPulseCheckboxChecked(true) : setPulseCheckboxChecked(false);

	useEffect(() => {
		if (firstLoad.current) {
			firstLoad.current = false;
			return;
		}
		isAllStepOpen ? focusSingleButton.current.focus() : focusHeader.current.focus();
	}, [isAllStepOpen]);


	/* This is the code that is responsible for setting the step image, title and copy. */
	useEffect(() => {
		if( (sessionStorage.getItem("demoCatName") === null || sessionStorage.getItem("demoCatName") === '') ){
            sessionStorage.setItem('demoCatName', content.categories[0].split('?')[0]);
        }
		
		if (step === -1) {
			setStepImage(lobRoot === '/ml'? t(demo_steps.firstStep[bankingType] + '-ml') : t(demo_steps.firstStep[bankingType]));
			setStepImgAlt(t(demo_steps.firstStep.lblImageAlt));
			setStepTitle(t(demo_steps.firstStep.lblGetStarted));
			setStepCopy(bankingType === SELECTORS.MB ? t(demo_steps.firstStep.txtGetStartedMB) : bankingType === SELECTORS.MPOS ? t(demo_steps.firstStep.txtGetStartedMPOS) : bankingType === SELECTORS.ESSENTIALS ? t(demo_steps.firstStep.txtGetStartedESSENTIALS) : bankingType === SELECTORS.RESTAURANT ? t(demo_steps.firstStep.txtGetStartedRESTAURANT) : t(demo_steps.firstStep.txtGetStartedOLB));
			return;
		}
		if (step === lastStep) {
			setStepImage(lobRoot === '/ml'? t(demo_steps.lastStep[bankingType] + '-ml') : t(demo_steps.lastStep[bankingType]));
			setStepImgAlt(t(demo_steps.lastStep.lblImageAlt));
			setStepTitle(t(demo_steps.lastStep.lblCompleted));
			setStepCopy(bankingType === SELECTORS.MB ? t(demo_steps.lastStep.txtCompletedMB) : bankingType === SELECTORS.OLB ? t(demo_steps.lastStep.txtCompletedOLB) : bankingType === SELECTORS.MPOS ? t(demo_steps.lastStep.txtCompletedMPOS) : bankingType === SELECTORS.ESSENTIALS ? t(demo_steps.lastStep.txtCompletedESSENTIALS) : t(demo_steps.lastStep.txtCompletedRESTAURANT));
			return;
		}
		setStepImage(sourceName + steps[step].image);
		setStepImgAlt(steps[step].altText);
		setStepTitle(steps[step].heading);
		setStepCopy(steps[step].copy);
		/*eslint-disable */
	}, [step, bankingType, t, lastStep, sourceName, steps]);

	return (
		isAllStepOpen
			// if TRUE shows All Steps of the Demo
			? <div className='demo-steps-container'>
					<div className={`demo-steps-container-wrapper all-steps-list banking-type--${ bankingType === SELECTORS.MB || bankingType === SELECTORS.MPOS ? SELECTORS.MB : SELECTORS.OLB}`}>
						<div className="steps-list-wrapper">
							<button ref={focusSingleButton} className="btn-return-single" onClick={viewAllSteps} dangerouslySetInnerHTML={{ __html: t(demo_steps.returnSingle )}}></button>
							<ul className="step-list">
								{
									steps.map((step, i) => <li className="step-list-item" key={i}>
										<div className="demo-step device-container">
											<div className="device-wrapper">
												<div className="image-wrapper" aria-hidden="true">
													<img src={sourceName + step.image} alt={step.altText} className="device-image" onLoad={imageLoaded} onError={imageNotLoaded} />
													<div className="device-wrapper--inner" style={imgDeviceStyle} />
													<div className={`device-wrapper--inner__status${(step === -1 || step === lastStep) ? '' : '-show'}`} />
												</div>
												{(isImageLoaded && i < lastStep) && <div className="hotspot-wrapper" style={{left: step.hotspot[0]+'%', top: step.hotspot[1]+'%'}}><div className="hotspot"></div></div>}
											</div>
										</div>
										<div className="demo-step text-wrapper">
											{ i === 0 && <div className="step-heading" dangerouslySetInnerHTML={{ __html: step.heading ? step.heading : t(demo_steps.txtGetStarted) }} /> }
											{i === lastStep && <div className="step-heading text--completed" dangerouslySetInnerHTML={{ __html: step.heading ? step.heading : t(demo_steps.txtCompleted) }} />}
											<div className="step-copy" dangerouslySetInnerHTML={{ __html: compileDisclosureText(step.copy).join('') }}></div>
										</div>
									</li>)
								}
							</ul>
							<button className="btn-return-single btn-return-single--bottom" onClick={viewAllSteps} dangerouslySetInnerHTML={{ __html: t(demo_steps.returnSingle )}}></button>
						</div>
					</div>
				</div>
			// if FALSE it will display single step
			: <div className='demo-steps-container'>
				<div className={`demo-steps-container-wrapper single-step banking-type--${bankingType === SELECTORS.MB || bankingType === SELECTORS.MPOS ? SELECTORS.MB : SELECTORS.OLB}`}>
					<div className="demo-step device-container">
						<div className="device-wrapper">
							<div className={`image-wrapper${(step === lastStep) ? ' img-clickable' : ''}`} onClick={(step === lastStep) ? nextState : null} aria-hidden="true">
								<img ref={imgDevice} src={(step === -1 || step === lastStep) ? `${process.env.PUBLIC_URL}`+stepImage : stepImage} alt={stepImgAlt} className="device-image" onLoad={imageLoaded} onError={imageNotLoaded} />
								<div className="device-wrapper--inner" style={imgDeviceStyle} />
								<div className={`device-wrapper--inner__status${(step === -1 || step === lastStep) ? '' : '-show'}`} />
								{(isImageLoaded && step < lastStep) && (<div className="hotspot-wrapper t-track-custom-click" data-bactmln={(step === -1) ? 'Demo-_-Click-_-GetStarted' : 'Next' && (step === lastStep) ? 'Demo-_-Click-_-LastStep' : 'End' && (step > -1) ? 'Demo-_-Click-_-Step'+(step+1) : 'Steps'} style={hotSpotStyles} onClick={nextState}><div className={"hotspot " + (isPulseCheckboxChecked ? 'pulse-active' : '')}></div></div>)}
							</div>
							<div className="pulse-switch">
								<span>{t(demo_steps.pulseAnimationText)}</span>
								<label className="switch">
								<input id="pulseAnimation" type="checkbox" onChange={handleChange} ref={pulseCheckboxState} defaultChecked={isPulseCheckboxChecked} aria-label={t(demo_steps.pulseAnimationText)} />
									<span className="slider round t-track-Animation t-track-custom-click" data-bactmln="Navigation-_-Click-_-Animation"></span>
								</label>
							</div>
						</div>
					</div>
					<div className="demo-step text-container">
						<div className="text-steps-container">
							<button className="btn btn-prev t-track-custom-click" onClick={prevState} aria-label={t(demo_steps.prevStep)} disabled={step > -1 ? false: true} data-bactmln={(step === -1) ? 'CarouselPrev-_-Click-_-GetStarted' : 'Next' && (step === lastStep) ? 'CarouselPrev-_-Click-_-LastStep' : 'End' && (step > -1) ? 'CarouselPrev-_-Click-_-Step'+(step+1) : 'Steps'}/>
							<div ref={focusHeader} className="text-box" tabIndex="-1">
								{ stepTitle && <div className="text--title" dangerouslySetInnerHTML={{ __html: stepTitle }} /> }
								<div ref={textCopy} className="text--copy" dangerouslySetInnerHTML={{ __html: compileDisclosureText(stepCopy).join('') }} />
							</div>	
							<button className="btn btn-next t-track-custom-click" onClick={nextState} aria-label={t(demo_steps.nextStep)} data-bactmln={(step === -1) ? 'CarouselNext-_-Click-_-GetStarted' : 'Next' && (step === lastStep) ? 'CarouselNext-_-Click-_-LastStep' : 'End' && (step > -1) ? 'CarouselNext-_-Click-_-Step'+(step+1) : 'Steps'}/>
							<div className="demo-step__out">
								<span>
								{ (step !== -1 && step !== lastStep) ? <span className="demo-step__counter" aria-live="polite">{t(demo_steps.step) + ' ' + (step+1) + ' ' + t(demo_steps.of) + ' ' + (steps.length)}</span> : '' }
								</span>
								<button className='view-all-steps t-track-custom-click' aria-label={t(demo_steps.viewAllHelpText)} onClick={viewAllSteps} dangerouslySetInnerHTML={{ __html: (step !== -1 && step !== lastStep) ? t(demo_steps.viewAllSteps)+ ' ' + t(demo_steps.steps) : t(demo_steps.viewAllSteps) +' '+  (steps.length) +' '+ t(demo_steps.steps)}} data-bactmln="Demo-_-Click-_-ViewAllSteps"/>
							</div>
						</div>
					</div>
				</div>
			</div>
	)
}

export default DemoSteps;